/**
 * Queue list state machine - replaces useQueueStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { Queue, QueueAgent, QueueCaller, QueueStats } from '../types'

export interface QueueListContext {
  queues: Queue[]
  selectedQueue: Queue | null
  isLoading: boolean
}

export type QueueListEvent =
  | { type: 'SET_QUEUES'; queues: Queue[] }
  | { type: 'SET_SELECTED_QUEUE'; queue: Queue | null }
  | { type: 'UPDATE_QUEUE'; id: string; updates: Partial<Queue> }
  | { type: 'UPDATE_QUEUE_STATS'; id: string; stats: QueueStats }
  | { type: 'ADD_AGENT'; queueId: string; agent: QueueAgent }
  | { type: 'UPDATE_AGENT'; queueId: string; agentId: string; updates: Partial<QueueAgent> }
  | { type: 'REMOVE_AGENT'; queueId: string; agentId: string }
  | { type: 'ADD_CALLER'; queueId: string; caller: QueueCaller }
  | { type: 'UPDATE_CALLER'; queueId: string; callerId: string; updates: Partial<QueueCaller> }
  | { type: 'REMOVE_CALLER'; queueId: string; callerId: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as QueueListContext,
    events: {} as QueueListEvent,
  },
})

export const queueListMachine = machineSetup.createMachine({
  id: 'queueList',
  context: {
    queues: [],
    selectedQueue: null,
    isLoading: false,
  },
  on: {
    SET_QUEUES: {
      actions: assign({
        queues: ({ event }) => (event.type === 'SET_QUEUES' ? event.queues : []),
      }),
    },
    SET_SELECTED_QUEUE: {
      actions: assign({
        selectedQueue: ({ event }) => (event.type === 'SET_SELECTED_QUEUE' ? event.queue : null),
      }),
    },
    UPDATE_QUEUE: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'UPDATE_QUEUE') return context.queues
          return context.queues.map((q) => (q.id === event.id ? { ...q, ...event.updates } : q))
        },
        selectedQueue: ({ context, event }) => {
          if (event.type !== 'UPDATE_QUEUE') return context.selectedQueue
          if (context.selectedQueue?.id !== event.id) return context.selectedQueue
          return { ...context.selectedQueue, ...event.updates }
        },
      }),
    },
    UPDATE_QUEUE_STATS: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'UPDATE_QUEUE_STATS') return context.queues
          return context.queues.map((q) => (q.id === event.id ? { ...q, stats: event.stats } : q))
        },
        selectedQueue: ({ context, event }) => {
          if (event.type !== 'UPDATE_QUEUE_STATS') return context.selectedQueue
          if (context.selectedQueue?.id !== event.id) return context.selectedQueue
          return { ...context.selectedQueue, stats: event.stats }
        },
      }),
    },
    ADD_AGENT: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'ADD_AGENT') return context.queues
          return context.queues.map((q) =>
            q.id === event.queueId ? { ...q, agents: [...q.agents, event.agent] } : q
          )
        },
      }),
    },
    UPDATE_AGENT: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'UPDATE_AGENT') return context.queues
          return context.queues.map((q) =>
            q.id === event.queueId
              ? {
                  ...q,
                  agents: q.agents.map((a) =>
                    a.id === event.agentId ? { ...a, ...event.updates } : a
                  ),
                }
              : q
          )
        },
      }),
    },
    REMOVE_AGENT: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'REMOVE_AGENT') return context.queues
          return context.queues.map((q) =>
            q.id === event.queueId
              ? { ...q, agents: q.agents.filter((a) => a.id !== event.agentId) }
              : q
          )
        },
      }),
    },
    ADD_CALLER: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'ADD_CALLER') return context.queues
          return context.queues.map((q) => {
            if (q.id !== event.queueId) return q
            const newCallers = [...q.callers, event.caller]
            return {
              ...q,
              callers: newCallers,
              stats: { ...q.stats, currentWaiting: newCallers.length },
            }
          })
        },
      }),
    },
    UPDATE_CALLER: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'UPDATE_CALLER') return context.queues
          return context.queues.map((q) =>
            q.id === event.queueId
              ? {
                  ...q,
                  callers: q.callers.map((c) =>
                    c.id === event.callerId ? { ...c, ...event.updates } : c
                  ),
                }
              : q
          )
        },
      }),
    },
    REMOVE_CALLER: {
      actions: assign({
        queues: ({ context, event }) => {
          if (event.type !== 'REMOVE_CALLER') return context.queues
          return context.queues.map((q) => {
            if (q.id !== event.queueId) return q
            const newCallers = q.callers.filter((c) => c.id !== event.callerId)
            return {
              ...q,
              callers: newCallers,
              stats: { ...q.stats, currentWaiting: newCallers.length },
            }
          })
        },
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    CLEAR: {
      actions: assign({
        queues: [],
        selectedQueue: null,
        isLoading: false,
      }),
    },
  },
})
